import { useAtom, useAtomValue } from "jotai";
import { useEffect } from "react";

import { openedDidomiModalState } from "~/atoms/didomi-modal";
import { heroScrollHintShownState, heroScrollHintVisibleState } from "~/atoms/hero-scroll-hint";
import useScrollDirection from "~/utils/use-scroll-direction";

const APPEAR_DELAY_MS = 4000;
const VISIBLE_DURATION_MS = 12000;

// Called once from Hero (the swiper container, never remounted by swiper's own loop/autoplay
// logic) so the appear/hide timers run for the lifetime of the page instead of being torn
// down and restarted every time a slide child remounts. Returns the shared "should it
// currently be visible" flag for HeroScrollHint instances to render.
export default function useHeroScrollHintScheduler(enabled: boolean) {
  const { y } = useScrollDirection({});
  const [visible, setVisible] = useAtom(heroScrollHintVisibleState);
  const [, setShown] = useAtom(heroScrollHintShownState);
  const didomiOpened = useAtomValue(openedDidomiModalState);

  useEffect(() => {
    // Don't start the countdown while the Didomi cookie banner covers the screen - the hint
    // would appear and auto-hide behind it, never actually seen.
    if (!enabled || didomiOpened) return;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let hideTimeout: ReturnType<typeof setTimeout> | undefined;
    const appearTimeout = setTimeout(() => {
      setShown((alreadyShown) => {
        if (alreadyShown) return true;
        setVisible(true);
        hideTimeout = setTimeout(() => setVisible(false), VISIBLE_DURATION_MS);
        return true;
      });
    }, APPEAR_DELAY_MS);

    return () => {
      clearTimeout(appearTimeout);
      clearTimeout(hideTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, didomiOpened]);

  useEffect(() => {
    if (y > 0) {
      setShown(true);
      setVisible(false);
    }
  }, [y, setShown, setVisible]);

  return visible;
}
